import { useMemo, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { CEPAS, CEPA_SINONIMOS } from '@escuela/ficha-domain'

interface CepaComboboxProps {
  /** Variedades seleccionadas (nombre canonico). */
  value: string[]
  onChange: (value: string[]) => void
  otros: string
  onOtrosChange: (value: string) => void
}

function normalize(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
}

/** Nombre canonico + posibles alias que lo referencian. */
interface CepaEntry {
  canonical: string
  haystacks: string[]
}

const ENTRIES: readonly CepaEntry[] = (() => {
  const map = new Map<string, string[]>()
  for (const c of CEPAS) map.set(c.nombre, [c.nombre])
  for (const [alias, canonical] of Object.entries(CEPA_SINONIMOS)) {
    const list = map.get(canonical) ?? [canonical]
    list.push(alias)
    map.set(canonical, list)
  }
  return Array.from(map.entries()).map(([canonical, haystacks]) => ({
    canonical,
    haystacks,
  }))
})()

/** Combobox con autocomplete para variedades de uva (multi + otros). */
export function CepaCombobox({
  value,
  onChange,
  otros,
  onOtrosChange,
}: CepaComboboxProps): ReactNode {
  const [query, setQuery] = useState('')

  const matches = useMemo(() => {
    const q = normalize(query)
    if (q.length === 0) return []
    return ENTRIES.filter(
      (e) =>
        !value.includes(e.canonical) &&
        e.haystacks.some((h) => normalize(h).includes(q)),
    ).slice(0, 8)
  }, [query, value])

  const add = (canonical: string): void => {
    if (!value.includes(canonical)) onChange([...value, canonical])
    setQuery('')
  }

  const remove = (canonical: string): void => {
    onChange(value.filter((v) => v !== canonical))
  }

  return (
    <div className="flex flex-col gap-3">
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {value.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-2.5 py-1 text-sm text-primary"
            >
              {v}
              <button
                type="button"
                aria-label={`Quitar ${v}`}
                onClick={() => remove(v)}
                className="hover:text-primary/70"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div className="relative">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar variedad de uva..."
          aria-label="Buscar variedad de uva"
        />
        {matches.length > 0 ? (
          <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover p-1 shadow-md">
            {matches.map((m) => (
              <li key={m.canonical}>
                <button
                  type="button"
                  onClick={() => add(m.canonical)}
                  className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
                >
                  <span>{m.canonical}</span>
                  {m.haystacks.length > 1 ? (
                    <span className="text-xs text-muted-foreground">
                      {m.haystacks.slice(1).join(', ')}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <Input
        value={otros}
        onChange={(e) => onOtrosChange(e.target.value)}
        placeholder="Otras variedades (texto libre)"
      />
    </div>
  )
}
